import React, { useEffect } from "react";
import { Image } from "react-bootstrap";
import { useAtom } from "jotai";
import { Challenge } from "@nestcoinco/hyper-api-gateway-api-client";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import "./ChallengeCard.scss";
import HyperCoin from "../../HyperCoin/HyperCoin";
import ImageTimeIcon from "../../../images/time-icon.svg";
import DangerButton from "../DangerButton/DangerButton";
import FilledButton from "../FilledButton/FilledButton";
import useCountdown from "../../../hooks/useCountdown";
import {
  selectedChallengeResponseAtom,
  useIsChallengeListModalOpen,
  useIsChallengeResponseModalOpen,
} from "../../../jotai/atoms/challenges";
import { QUERY_KEYS } from "../../../react-query/constants";
import useHprFiatFormatter from "../../../hooks/useHprFiatFormatter";

function ChallengeCard({
  isReducedSize,
  challenge,
}: {
  isReducedSize?: boolean;
  challenge: Challenge;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { isFiatDisplay, formatHprToFiatValue } = useHprFiatFormatter();
  const [, setSelectedChallengeResponse] = useAtom(
    selectedChallengeResponseAtom
  );
  const { closeChallengeListModal } = useIsChallengeListModalOpen();
  const { openChallengeResponseModal } = useIsChallengeResponseModalOpen();
  const thumbnail = challenge?.game?.thumbnail;
  const stakeAmount = challenge?.stakeAmount;
  const rewardAmount = challenge?.rewardAmount;
  const declinePenalty = challenge?.declinePenalty;
  const endDate = challenge?.expiresAt;
  const avatar = challenge?.initiator?.avatarUrl;
  const challengerUsername = challenge?.initiator.username;
  const timeLeft = useCountdown({ futureDateOrTotalTimeInMs: endDate });

  useEffect(() => {
    if (!timeLeft) {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CHALLENGE.PENDING],
      });
    }
  }, [queryClient, timeLeft]);

  return (
    <div
      className="challenge-card"
      style={
        isReducedSize
          ? {
              height: 114,
              minHeight: 114,
              border: "none",
              background: "#FAFAFA",
            }
          : undefined
      }
    >
      <div className="d-flex justify-content-end"></div>
      <div className="d-flex gap-2 content">
        <div className="position-relative mb-3">
          <Image
            src={avatar}
            alt="avatar"
            className="rounded-img avatar"
            style={isReducedSize ? { height: 30, width: 30 } : undefined}
          />
          <Image
            src={thumbnail}
            alt="game thumbnail"
            className="rounded-img game-thumbnail"
            style={isReducedSize ? { height: 30, width: 30 } : undefined}
          />
        </div>
        <div className="w-100 d-flex flex-column">
          <h5>{`@${challengerUsername} ${t("challenged_you")}!`}</h5>
          <div className="d-flex align-items-center gap-2">
            <div className="d-flex">
              <p className="reward">{t("winner_gets")}</p>
              <p className="hpr d-flex align-items-center">
                {!isFiatDisplay && <HyperCoin width={12} height={15} />}
                <span>
                  {isFiatDisplay
                    ? formatHprToFiatValue(rewardAmount)
                    : rewardAmount}
                </span>
              </p>
            </div>
            <span className="dot" />
            <div className="expires-in-text">
              <Image src={ImageTimeIcon} alt="clock" /> <span>{timeLeft}</span>
            </div>
          </div>
        </div>
      </div>
      <div
        className="d-flex btn-box"
        style={isReducedSize ? { justifyContent: "end", gap: 12 } : undefined}
      >
        <DangerButton
          className="cancel-btn"
          onClick={() => {
            setSelectedChallengeResponse({ isDecline: true, challenge });
            openChallengeResponseModal();
            closeChallengeListModal();
          }}
          style={
            isReducedSize
              ? {
                  height: 32,
                  background: "white",
                  fontSize: ".875rem",
                  fontWeight: 500,
                }
              : undefined
          }
        >
          {t("decline")}{" "}
          <>
            {!isFiatDisplay && <HyperCoin className="ms-1" />} -
            {isFiatDisplay
              ? formatHprToFiatValue(declinePenalty ?? stakeAmount)
              : declinePenalty ?? stakeAmount}
          </>
        </DangerButton>
        <FilledButton
          className="accept-btn"
          onClick={() => {
            setSelectedChallengeResponse({ isDecline: false, challenge });
            openChallengeResponseModal();
            closeChallengeListModal();
          }}
          style={
            isReducedSize
              ? {
                  height: 32,
                  fontSize: ".875rem",
                  fontWeight: 500,
                }
              : undefined
          }
        >
          {t("accept")}{" "}
          <>
            {!isFiatDisplay && <HyperCoin className="ms-1" />}{" "}
            {isFiatDisplay ? formatHprToFiatValue(stakeAmount) : stakeAmount}
          </>
        </FilledButton>
      </div>
    </div>
  );
}

export default ChallengeCard;
